import { ChevronDown } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { AvailableToIdsPopoverProps } from '@/types/predefined-messages';

import { CheckboxWithLabel } from '@/components/common/checkbox-with-label';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  getAllAvailableToTargetIds,
  getAvailableToIdsAfterToggleAll,
  PREDEFINED_MESSAGE_FORM_FILTER_SURFACE_CLASS,
  toggleValueInList,
} from '@/lib/helpers/predefined-messages';
import { cn } from '@/lib/utils';

export function AvailableToIdsPopover({
  value,
  onChange,
  open,
  onOpenChange,
  availableToTargets,
  summary,
}: AvailableToIdsPopoverProps) {
  const { t } = useTranslation();
  const allRoleId = 'all';
  const selected = new Set(value);
  const options = availableToTargets.map(({ id, label }) => ({ id, label }));

  const allIds = useMemo(
    () =>
      getAllAvailableToTargetIds({
        targets: availableToTargets.filter(({ id }) => id !== allRoleId),
      }),
    [availableToTargets, allRoleId]
  );

  const showAll = useMemo(
    () =>
      options.some((option) => option.id !== allRoleId) &&
      options.every((option) => option.id === allRoleId || selected.has(option.id)),
    [options, selected]
  );

  function setAll(checked: boolean) {
    onChange(getAvailableToIdsAfterToggleAll({ checked, allTargetIds: allIds }));
  }

  function toggleId(id: string) {
    onChange(toggleValueInList({ current: value, id }));
  }

  function onCheckbox(id: string, checked: boolean) {
    if (id === allRoleId) {
      setAll(checked);

      return;
    }

    toggleId(id);
  }

  function handleOptionClick(id: string, isAllSelected: boolean) {
    return () => onCheckbox(id, isAllSelected ? !showAll : !selected.has(id));
  }

  function handleOpenAutoFocus(event: Event) {
    event.preventDefault();
  }

  function handleWheel(event: React.WheelEvent) {
    event.stopPropagation();
  }

  function getChecked(id: string, isAllSelected: boolean) {
    return isAllSelected ? showAll : selected.has(id);
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            'hover:bg-muted h-9 w-full justify-between px-3 font-normal',
            PREDEFINED_MESSAGE_FORM_FILTER_SURFACE_CLASS
          )}
        >
          <span className="min-w-0 flex-1 truncate text-left">{summary}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          'w-full min-w-(--radix-popover-trigger-width) p-0',
          PREDEFINED_MESSAGE_FORM_FILTER_SURFACE_CLASS
        )}
        onOpenAutoFocus={handleOpenAutoFocus}
        onWheel={handleWheel}
      >
        <div
          className="scrollbar-custom flex max-h-64 flex-col overflow-auto p-2"
          role="group"
          aria-label={t('configuration.predefinedMessages.columns.availableTo')}
        >
          {options.map(({ id, label }) => {
            const isAllSelected = id === allRoleId;

            return (
              <Button
                variant="ghost"
                key={id}
                asChild
                className="hover:bg-accent h-auto w-full justify-start rounded-sm p-2"
                onClick={handleOptionClick(id, isAllSelected)}
              >
                <div className="flex items-center gap-2">
                  <CheckboxWithLabel
                    label={label}
                    checked={getChecked(id, isAllSelected)}
                    className="pointer-events-none"
                  />
                </div>
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
